/**
 * Scenario Well Detail Modal
 * Shows detailed information about a scenario well including production data
 */
import {
    Close,
    OilBarrel,
    TrendingUp,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Typography,
} from '@mui/material';
import React from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ScenarioWell } from '@/api/scenarioWells';

interface ScenarioWellDetailModalProps {
  open: boolean;
  onClose: () => void;
  well: ScenarioWell | null;
  readOnly?: boolean;
}

interface WellProductionData {
  month: number;
  oil: number;
  gas: number;
  injection: number;
}

export const ScenarioWellDetailModal: React.FC<ScenarioWellDetailModalProps> = ({
  open,
  onClose,
  well,
  readOnly = false,
}) => {
  if (!well) return null;

  // Get well type name
  const wellTypeName = typeof well.well_type === 'object' ? well.well_type?.name : well.well_type;

  // Mock production data - replace with actual AI calculations
  const productionData: WellProductionData[] = [];
  for (let month = 1; month <= 120; month++) {
    const declineRate = 0.08;
    const oil = (well.ini_oil || 1000) * Math.exp(-declineRate * month / 12);
    const gas = (well.ini_gas || 500) * Math.exp(-declineRate * month / 12);
    const injection = wellTypeName === 'injection' ? 800 : 0;

    productionData.push({
      month,
      oil,
      gas,
      injection,
    });
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'production':
        return 'success';
      case 'injection':
        return 'info';
      case 'investigation':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <OilBarrel color="primary" />
          <Typography variant="h6">
            Well Details: {well.well?.name || well.well_name}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Well Name
                  </Typography>
                  <Typography variant="body1">
                    {well.well?.name || well.well_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Chip
                    label={well.well_type?.name || well.well_type || 'Unknown'}
                    color={getTypeColor(well.well_type?.name || well.well_type || '') as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Reservoir
                  </Typography>
                  <Typography variant="body1">
                    {well.layer_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Entry Date
                  </Typography>
                  <Typography variant="body1">
                    {well.entry_date ? new Date(well.entry_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Initial Production Parameters */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Initial Production Parameters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Initial Oil Production
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {well.ini_oil ? `${formatNumber(well.ini_oil)} bbl/day` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Initial Gas Production
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    {well.ini_gas ? `${formatNumber(well.ini_gas)} Mcf/day` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Well Type
                  </Typography>
                  <Typography variant="h6">
                    {wellTypeName ? wellTypeName.charAt(0).toUpperCase() + wellTypeName.slice(1) : 'Unknown'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Production Forecast */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp color="primary" />
                Production Forecast (AI-Generated)
              </Typography>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={productionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(value) => formatNumber(value)} />
                    <Tooltip formatter={(value: number) => formatNumber(value)} />
                    <Legend />

                    {wellTypeName === 'production' && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="oil"
                          stroke="#4caf50"
                          strokeWidth={2}
                          name="Oil (bbl/day)"
                        />
                        <Line
                          type="monotone"
                          dataKey="gas"
                          stroke="#2196f3"
                          strokeWidth={2}
                          name="Gas (Mcf/day)"
                        />
                      </>
                    )}

                    {wellTypeName === 'injection' && (
                      <Line
                        type="monotone"
                        dataKey="injection"
                        stroke="#9c27b0"
                        strokeWidth={2}
                        name="Injection (bbl/day)"
                      />
                    )}

                    {wellTypeName === 'investigation' && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="oil"
                          stroke="#4caf50"
                          strokeWidth={2}
                          name="Oil (bbl/day)"
                        />
                        <Line
                          type="monotone"
                          dataKey="gas"
                          stroke="#2196f3"
                          strokeWidth={2}
                          name="Gas (Mcf/day)"
                        />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Well Performance Summary */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Well Performance Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Peak Production
                  </Typography>
                  <Typography variant="h6">
                    {well.ini_oil ? `${formatNumber(well.ini_oil)} bbl/day` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Decline Rate
                  </Typography>
                  <Typography variant="h6">
                    8% annually
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Economic Life
                  </Typography>
                  <Typography variant="h6">
                    15 years
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Cumulative Production
                  </Typography>
                  <Typography variant="h6">
                    {well.ini_oil ? `${formatNumber(well.ini_oil * 15 * 365)} bbl` : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
